/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { CircleX } from 'lucide-react';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';

import type { ShopperBasketsV2 } from '@/scapi';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { FormInput } from '@/components/form-fields';
import { createPromoCodeFormSchema, type PromoCodeFormData } from '@/components/promo-code-form';
import { useToast } from '@/components/toast';
import { usePromoCodeActions } from '@/hooks/use-promo-code-actions';
import { useBasketUpdater } from '@/providers/basket';
import { FETCHER_STATES } from '@/lib/fetcher-states';
import { getCouponDiscount, isCouponApplied } from '@/lib/cart/coupon-status';
import { formatCurrency } from '@/lib/currency';
import type { BasketActionResponse } from '@/routes/types/action-responses';

type Basket = ShopperBasketsV2.schemas['Basket'];
type CouponItem = NonNullable<Basket['couponItems']>[number];

const PROMO_CODE_ACCORDION_VALUE = 'promo-code';

/**
 * Cart coupon section of the Price Shoes order summary.
 *
 * The cart accepts a single coupon: while no coupon is applied it shows the collapsed
 * "Agregar código de descuento" accordion with the code field; once a coupon produces a discount the
 * accordion is replaced by the "Cupón agregado" row with its amount and a remove button.
 */
export default function CartPromoCode({ basket }: { basket: Basket }): ReactElement {
    const appliedCoupon = useMemo(() => basket.couponItems?.find(isCouponApplied), [basket.couponItems]);

    if (appliedCoupon) {
        return <AppliedCouponRow basket={basket} coupon={appliedCoupon} />;
    }
    return <PromoCodeAccordion basket={basket} />;
}

function PromoCodeAccordion({ basket }: { basket: Basket }): ReactElement {
    const { t } = useTranslation('cart');
    const { applyPromoCode, applyFetcher } = usePromoCodeActions(basket.basketId);
    const { addToast } = useToast();
    const updateBasket = useBasketUpdater();
    const schema = useMemo(() => createPromoCodeFormSchema(t), [t]);
    const isApplying = applyFetcher.state === FETCHER_STATES.SUBMITTING;

    const form = useForm<PromoCodeFormData>({
        resolver: zodResolver(schema),
        defaultValues: { code: '' },
    });

    useEffect(() => {
        const data = applyFetcher.data as BasketActionResponse | undefined;
        if (!data) {
            return;
        }
        if (data.success) {
            if (data.basket) {
                updateBasket(data.basket);
            }
            form.reset({ code: '' });
            addToast(t('promoCode.successMessage'), 'success');
        } else {
            // Prefer the server's status-specific message, falling back to the generic one.
            const errorMessage = data.error?.message || t('promoCode.errorMessage');
            form.setError('code', { type: 'manual', message: errorMessage });
            addToast(errorMessage, 'error');
        }
        // addToast is stable and does not need to be in the dependency array
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [applyFetcher.data, form, t, updateBasket]);

    const handleSubmit = form.handleSubmit((data) => {
        if (!basket.basketId) {
            form.setError('code', { type: 'manual', message: t('promoCode.noBasketMessage') });
            return;
        }
        applyPromoCode(data.code);
    });

    return (
        <Accordion type="single" collapsible className="w-full">
            <AccordionItem value={PROMO_CODE_ACCORDION_VALUE} className="border-b-0">
                <AccordionTrigger
                    onClick={() => form.reset()}
                    className="items-center px-[5px] py-1 text-xs font-medium leading-5 text-primary [&>svg]:size-[18px] [&>svg]:translate-y-0 [&>svg]:text-primary">
                    {t('promoCode.addDiscountCode')}
                </AccordionTrigger>
                <AccordionContent className="pt-3 pb-0">
                    <Form {...form}>
                        <form
                            onSubmit={(e) => void handleSubmit(e)}
                            data-testid="cart-promo-code-form"
                            className="[--ui-radius:var(--radius-lg)]">
                            <FormField
                                control={form.control}
                                name="code"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="sr-only">{t('promoCode.label')}</FormLabel>
                                        <div className="flex gap-3">
                                            <FormInput
                                                placeholder={t('promoCode.examplePlaceholder')}
                                                className="h-10 px-3.5 text-base focus-visible:ring-0 md:text-base"
                                                {...field}
                                            />
                                            <Button
                                                type="submit"
                                                disabled={isApplying}
                                                className="h-10 px-4 text-sm font-normal leading-[22px]">
                                                {isApplying ? t('promoCode.adding') : t('promoCode.add')}
                                            </Button>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </form>
                    </Form>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}

function AppliedCouponRow({ basket, coupon }: { basket: Basket; coupon: CouponItem }): ReactElement {
    const { t, i18n } = useTranslation('cart');
    const { currency: siteCurrency } = useSite();
    const { removePromoCode, removeFetcher } = usePromoCodeActions(basket.basketId);
    const { addToast } = useToast();
    const updateBasket = useBasketUpdater();
    const isRemoving = removeFetcher.state !== FETCHER_STATES.IDLE;

    useEffect(() => {
        const data = removeFetcher.data as BasketActionResponse | undefined;
        if (!data) {
            return;
        }
        if (data.success) {
            if (data.basket) {
                updateBasket(data.basket);
            }
            addToast(t('promoCode.removeSuccessMessage'), 'success');
        } else if (data.error) {
            addToast(t('promoCode.removeErrorMessage'), 'error');
        }
        // addToast is stable and does not need to be in the dependency array
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [removeFetcher.data, t, updateBasket]);

    const discount = getCouponDiscount(basket, coupon.code);

    return (
        <div className="flex w-full items-center gap-6" data-testid="cart-applied-coupon">
            <span className="text-xs font-medium leading-6 whitespace-nowrap text-primary">
                {t('promoCode.couponApplied')}:
            </span>
            <div className="flex flex-1 items-center justify-end">
                <span className="text-base font-light leading-6 text-primary">
                    {formatCurrency(discount, i18n.language, basket.currency ?? siteCurrency)}
                </span>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('promoCode.removeCoupon', { code: coupon.code })}
                    disabled={isRemoving}
                    className="size-7 p-[5px] [--ui-radius:9999px] text-primary hover:text-primary [&_svg:not([class*='size-'])]:size-[18px]"
                    onClick={() => {
                        if (coupon.couponItemId) {
                            removePromoCode(coupon.couponItemId);
                        }
                    }}>
                    <CircleX />
                </Button>
            </div>
        </div>
    );
}
